import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Rating,
  Box,
  Skeleton,
  Typography,
  Collapse,
  Chip,
  Grid2,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTheme } from '@mui/material/styles';
import { ReactComponent as VerifiedIcon } from '../../../assets/icons/bages/varify_small.svg';
import { ReactComponent as EarlyBirdIcon } from '../../../assets/icons/bages/early_bird.svg';
import GradeIcon from '@mui/icons-material/Grade';
import {
  ButtonTertiary,
  GlassColors,
  NeumorphicButton,
} from '../../../components/glassmorphism/GlassmorphismStyles';
import { getCategoryClass } from '../../../configuration/categoryConfigIcon';
import {
  HandymanViewModel,
  ReviewViewModel,
  UserPortfolioItemViewModel,
} from '../../../configuration/api/apiSchema';
// import { connect } from 'react-redux';
// import { getUserPortfolio } from '../../UserProfile/components/UserPortfolio/actions';
// import { HandymanAppState } from '../../../configuration/redux/reducers';
import ReviewsList from '../../../components/Reviews/ReviewsList';
import { connect } from 'react-redux';
import { HandymanAppState } from '../../../configuration/redux/reducers';

interface Handyman {
  id: string;
  avatarUrl?: string;
  name: string;
  isVerified: boolean;
  registrationDate: string;
  lastOnline: string;
  rating: number;
  positiveFeedbackPercent: number;
  reviewsCount: number;
  shortDescription: string;
  ratePerHour?: number;
  fixedPriceRate?: number;
  categories: string[];
  city: string;
  cityDistrict?: string;
  userPortfolioItems: UserPortfolioItemViewModel[];
  kycStatus?: boolean;
  isStripeAccountVerified?: boolean;
  isEarlyBird?: boolean;
}

interface Category {
  key: string;
  name: string;
  root: string;
  level: number;
}

interface HandymanCardProps {
  handyman?: Handyman;
  onAction?: (
    id?: string,
    handyman?: HandymanViewModel,
    selectedServices?: string[],
  ) => void;
  actionButtonTitle?: string;
  isSkeleton?: boolean;
  categoryDictionary?: Record<string, string>;
  categories: Record<string, Category>;
  isNotHandyman?: boolean;
  lastReviews?: ReviewViewModel[];
  reviewsLoading?: boolean;
  onNeedReviews?: (handymanId: string) => void;
  // ensurePortfolioLoaded?: (handymanId: string) => void;
  userPortfolioItems?: UserPortfolioItemViewModel[] | null;
  userReviews: ReviewViewModel[];
  isCategoriesVisible?: boolean;
  sentProposalIds?: string[];
}

const HandymanCard: React.FC<HandymanCardProps> = React.memo(
  ({
    handyman,
    onAction,
    actionButtonTitle,
    isSkeleton = false,
    categoryDictionary = {},
    categories = {},
    lastReviews = [],
    reviewsLoading = true,
    onNeedReviews,
    isNotHandyman,
    userPortfolioItems,
    userReviews,
    isCategoriesVisible,
    sentProposalIds,
  }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const navigate = useNavigate();
    const [cardDetailsOpened, setCardDetailsOpened] = useState(false);

    const handleActionClick = React.useCallback(() => {
      if (onAction && handyman) {
        onAction(handyman.id);
      }
    }, [onAction, handyman]);

    const selectedCategories: Category[] | undefined = handyman?.categories
      .map((key) => categories[key])
      .filter((cat) => cat.level === 1);

    let moreToSeeCategories = 0;

    if (selectedCategories && selectedCategories.length > 3) {
      moreToSeeCategories = selectedCategories.length - 3;
    }

    const toggleCard = (handymanId: string) => {
      setCardDetailsOpened((prev) => {
        const next = !prev;
        if (next) {
          onNeedReviews?.(handymanId);
        }
        return next;
      });
    };

    const selectedCategoriesToSee: Category[] | undefined = cardDetailsOpened
      ? selectedCategories
      : selectedCategories?.slice(0, 3);

    if (isSkeleton) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '16px 24px 24px',
            gap: '10px',
            background: isDark ? '#1A1A1A' : '#E5E5DC',
            boxShadow:
              '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF',
            borderRadius: '32px',
            mb: 2,
            height: '238px',
          }}
        >
          <Skeleton variant='circular' width={40} height={40} />
          <Skeleton variant='text' width='60%' height={32} />
          <Skeleton variant='text' width='80%' height={20} />
          <Skeleton variant='text' width='100%' height={24} />
          <Skeleton
            variant='rectangular'
            width='100%'
            height={56}
            sx={{ borderRadius: '16px' }}
          />
        </Box>
      );
    }

    if (!handyman || !onAction || !actionButtonTitle) {
      return null;
    }

    const isInvited = sentProposalIds?.includes(handyman.id);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: { xs: '16px', md: '16px 24px 24px' },
          gap: '10px',
          background: isDark ? '#1A1A1A' : '#E5E5DC',
          boxShadow:
            '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF',
          borderRadius: '32px',
          mb: 2,
        }}
      >
        {/* Header - Avatar and Name */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '0px',
            gap: '16px',
            width: '100%',
          }}
        >
          <Link
            to={`/browse-handyman/${handyman.id}`}
            style={{ textDecoration: 'none' }}
          >
            <Avatar
              src={handyman.avatarUrl}
              alt={handyman.name}
              sx={{
                width: 40,
                height: 40,
                cursor: 'pointer',
                backgroundColor: isDark
                  ? 'rgba(0, 92, 173, 0.7)'
                  : 'rgba(0, 36, 58, 0.8)',
                color: '#00FFFF',
                boxShadow: `
                -2.82px -2.82px 8.49px 0px #FFFFFF4D,
                14.15px 14.15px 22.64px -5.37px #0000001A,
                -7.54px -7.54px 8.66px 0px #FFFFFF33,
                5px 5px 24px -11.7px #00000033
                `,
              }}
            />
          </Link>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '0px',
              gap: '4px',
              flex: 1,
              minWidth: 0,
            }}
          >
            {/* Name and Badge */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0px',
                gap: { xs: 0, md: '8px' },
                maxWidth: '100%',
                '& svg path': {
                  fill: isDark ? GlassColors.accent : GlassColors.secondary,
                },
              }}
            >
              <Link
                to={`/browse-handyman/${handyman.id}`}
                style={{
                  textDecoration: 'none',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'Montserrat',
                    fontStyle: 'normal',
                    fontWeight: 800,
                    fontSize: '20px',
                    lineHeight: '160%',
                    letterSpacing: '0.15px',
                    color: isDark ? '#B6B6B6' : '#1A1A1A',
                    cursor: 'pointer',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    mr: { xs: '4px', md: 0 },
                    '&:hover': {
                      color: isDark ? '#00FFFF' : '#005CAD',
                    },
                  }}
                >
                  {handyman.name}
                </Box>
              </Link>
              <Box sx={{ display: 'flex', gap: '4px' }}>
                {handyman.kycStatus === true &&
                  handyman.isStripeAccountVerified === true && <VerifiedIcon />}
                {handyman.isEarlyBird && <EarlyBirdIcon />}
              </Box>
            </Box>
            {/* Registration Info or Rating Section */}
            {cardDetailsOpened ? (
              <Box
                sx={{
                  fontFamily: 'Roboto',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '143%',
                  letterSpacing: '0.17px',
                  color: isDark ? '#D5D5D5' : '#393939',
                }}
              >
                Registered: {handyman.registrationDate} • Last Online:{' '}
                {handyman.lastOnline}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  padding: '0px',
                  gap: '8px',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: '0px',
                    gap: '4px',
                  }}
                >
                  <Rating
                    name={`rating-${handyman.id}`}
                    value={handyman.rating || 0}
                    precision={0.1}
                    readOnly
                    icon={<GradeIcon fontSize='inherit' />}
                    emptyIcon={<GradeIcon fontSize='inherit' />}
                    sx={{
                      filter: 'drop-shadow(0px 2px 1px rgba(0,0,0,0.5))',
                      '& .MuiRating-icon': {
                        fontSize: '18px',
                      },
                      '& .MuiRating-iconFilled': {
                        color: '#D1AB01',
                      },
                      '& .MuiRating-iconEmpty': {
                        color: '#D1D0C6',
                      },
                    }}
                  />
                  <Box
                    sx={{
                      fontFamily: 'Montserrat',
                      fontStyle: 'normal',
                      fontSize: '12px',
                      lineHeight: '166%',
                      letterSpacing: '.4px',
                      textTransform: 'uppercase',
                      color: isDark ? '#D5D5D5' : '#393939',
                    }}
                  >
                    ({handyman.rating})
                  </Box>
                </Box>

                <Box
                  sx={{
                    fontFamily: 'Montserrat',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    fontSize: '12px',
                    lineHeight: '166%',
                    letterSpacing: '.4px',
                    color: isDark ? '#D5D5D5' : '#393939',
                  }}
                >
                  {handyman.reviewsCount} reviews
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Rating Section in opened card */}
        {cardDetailsOpened && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              padding: '0px',
              gap: '8px',
              width: '100%',
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0px',
                gap: '8px',
              }}
            >
              <Rating
                name={`rating-${handyman.id}`}
                value={handyman.rating}
                precision={0.1}
                readOnly
                size={'large'}
                icon={<GradeIcon fontSize='inherit' />}
                emptyIcon={<GradeIcon fontSize='inherit' />}
                sx={{
                  filter: 'drop-shadow(0px 2px 1px rgba(0,0,0,0.5))',
                  '& .MuiRating-icon': {
                    fontSize: '30px',
                  },
                  '& .MuiRating-iconFilled': {
                    color: '#D1AB01',
                  },
                  '& .MuiRating-iconEmpty': {
                    color: '#D1D0C6',
                  },
                }}
              />
              <Box
                sx={{
                  fontFamily: 'Montserrat',
                  fontStyle: 'normal',
                  fontWeight: 900,
                  fontSize: '12px',
                  lineHeight: { xs: '18px', sm: '266%' },
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: isDark ? '#D5D5D5' : '#585858',
                }}
              >
                ({handyman.rating}/5)
              </Box>
            </Box>

            <Box
              sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '19px',
                color: isDark ? '#D5D5D5' : '#585858',
              }}
            >
              {handyman.reviewsCount} reviews •{' '}
              {handyman.positiveFeedbackPercent}% positive
            </Box>
          </Box>
        )}

        {/* Categories bages */}
        {isCategoriesVisible && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {selectedCategoriesToSee?.map((cat) => (
              <Box
                key={cat.key}
                sx={{
                  background: getCategoryClass(cat.root)[0],
                  color:
                    getCategoryClass(cat.root)[1] === 'dark'
                      ? '#393939'
                      : '#E5E5DC',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: 2,
                  fontFamily: 'Montserrat, sans-serif',
                  p: '2px 16px',
                  borderRadius: '32px',
                  boxShadow: `0px 0px 16px 0px #5C5C5C4D inset,
                0px 0px 3px 0px #FFFFFF66 inset,
                -1px -1px 0.5px -1px #FFFFFF inset,
                1px 1px 0.5px -1px #FFFFFF66 inset,
                -1px -1px 0px -0.5px #FFFFFF80 inset,
                1px 1px 0px -0.5px #FFFFFF99 inset,
                0px 1px 8px 0px #0000001F,
                0px 0px 2px 0px #0000001A
                `,
                }}
              >
                {cat.name}
              </Box>
            ))}
            {!cardDetailsOpened && moreToSeeCategories ? (
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: '28px',
                  fontFamily: 'roboto, sans-serif',
                  fontWeight: 700,
                  alignSelf: 'end',
                }}
              >
                and {moreToSeeCategories} more
              </Typography>
            ) : (
              ''
            )}
          </Box>
        )}
        {/* Description */}
        <Box
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: { xs: '14px', md: '16px' },
            lineHeight: '150%',
            letterSpacing: '0.15px',
            color: isDark ? '#B6B6B6' : '#1A1A1A',
            width: '100%',
          }}
        >
          {handyman.shortDescription}
        </Box>
        {/* Bottom Section - Prices and Button */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0px',
            gap: '10px',
            width: '100%',
          }}
        >
          {/* Prices - Stacked Vertically */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {handyman.ratePerHour != null && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  padding: '0px',
                  gap: '8px',
                  mr: '10px',
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: { xs: '14px', md: '16px' },
                    lineHeight: '150%',
                    letterSpacing: '0.15px',
                    color: isDark ? '#B6B6B6' : '#1A1A1A',
                  }}
                >
                  Hourly Rate:
                </Box>
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 700,
                    fontSize: { xs: '14px', md: '16px' },
                    lineHeight: '175%',
                    textAlign: 'right',
                    letterSpacing: '0.15px',
                    color: isDark ? '#00FFFF' : '#005CAD',
                  }}
                >
                  £{handyman.ratePerHour}/h
                </Box>
              </Box>
            )}

            {handyman.fixedPriceRate != null && cardDetailsOpened && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  padding: '0px',
                  gap: '8px',
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: { xs: '14px', md: '16px' },
                    lineHeight: '150%',
                    letterSpacing: '0.15px',
                    color: isDark ? '#B6B6B6' : '#1A1A1A',
                  }}
                >
                  Fixed Price
                </Box>
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    fontSize: { xs: '14px', md: '16px' },
                    lineHeight: '175%',
                    textAlign: 'right',
                    letterSpacing: '0.15px',
                    color: isDark ? '#00FFFF' : '#005CAD',
                  }}
                >
                  £{handyman.fixedPriceRate}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Collapse
          in={cardDetailsOpened}
          timeout='auto'
          unmountOnExit
          sx={{
            width: '100%',
          }}
        >
          {handyman.userPortfolioItems.length > 0 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '5px',
                  mb: '16px',
                  mt: '14px',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '20px',
                    fontWeight: 800,
                    lineHeight: '160%',
                    letterSpacing: '.15px',
                    color: isDark
                      ? GlassColors.dark.textPrimary
                      : GlassColors.light.textPrimary,
                  }}
                >
                  Portfolio
                </Typography>
                <ButtonTertiary
                  onClick={() =>
                    navigate(`/browse-handyman/${handyman.id}/?activeTab=2`)
                  }
                  sx={{
                    fontSize: '14px',
                    ml: 'auto',
                  }}
                >
                  View Full Portfolio
                </ButtonTertiary>
              </Box>
              <Box
                sx={{
                  p: { xs: '16px', md: '24px 24px 32px' },
                  borderRadius: '32px',
                  mb: '24px',
                  boxShadow: `
                -2.82px -2.82px 8.49px 0px #FFFFFF4D,
                14.15px 14.15px 22.64px -0.37px #0000001A,
                -17.54px -17.54px 28.66px 0px #FFFFFF0D,
                10px 10px 28.66px -11.7px #00000033,
                1.58px 1.58px 1.7px 0px #FFFFFF inset,
                -2.07px -2.64px 4.52px -1.51px #0000004D inset
              `,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '5px',
                    mb: '16px',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: { xs: '16px', sm: '20px' },
                      fontWeight: { xs: 700, sm: 800 },
                      lineHeight: '160%',
                      letterSpacing: '.15px',
                      color: isDark
                        ? GlassColors.dark.textPrimary
                        : GlassColors.light.textPrimary,
                    }}
                  >
                    Portfolio item
                  </Typography>

                  {handyman.isVerified && (
                    <Chip
                      sx={{
                        fontSize: '12px',
                        lineHeight: '18,px',
                        p: '2px 16px',
                        border: 0,
                        ml: 'auto',
                        color: GlassColors.textPrimary,
                        boxShadow: `
                      0px 0px 34.77px 0px #0000000D inset,
                      0px -0.5px 0.25px 0px #00000080 inset,
                      0px 0.5px 0.75px 0px #FFFFFF inset,
                      0px 2.07px 3.73px 0px #0000004D,
                      0px 2.08px 2.48px -2px #00000066,
                      0px 3.08px 2px -2px #00000054
                    `,
                        '&& .MuiChip-label': {
                          color: 'inherit',
                        },
                        '&&': {
                          backgroundColor: GlassColors.additional,
                        },
                      }}
                      label='Verifed'
                    />
                  )}
                </Box>
                <Box>
                  {handyman.userPortfolioItems.length ? (
                    <Grid2
                      sx={{ width: '100%' }}
                      columnSpacing={{ xs: 1, md: 2 }}
                      rowSpacing={{ xs: 1, md: 2 }}
                      container
                    >
                      {handyman.userPortfolioItems?.map((portfolioItem) =>
                        portfolioItem.base64Images?.map((img, idx) => (
                          <Grid2
                            key={`${portfolioItem.id}-${idx}`}
                            size={{ xs: 12, sm: 6 }}
                            sx={{
                              '& img': {
                                maxWidth: '100%',
                                borderRadius: '32px',
                                boxShadow: `
                          0px 0px 34.77px 0px #00000033 inset,
                          0px -0.5px 0.25px 0px #0000004D inset,
                          0px 0.5px 0.75px 0px #FFFFFF66 inset,
                          0px 2.07px 3.73px 0px #00000066,
                          0px 1.08px 2.48px 0px #000000
                          `,
                              },
                            }}
                          >
                            <img
                              src={img}
                              alt={
                                portfolioItem.description ||
                                'Handyman portfolio photo'
                              }
                            />
                          </Grid2>
                        )),
                      )}
                    </Grid2>
                  ) : (
                    <Typography>There is nothing here.</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {userReviews.length > 0 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '5px',
                  mb: '16px',
                  mt: '14px',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '20px',
                    fontWeight: 800,
                    lineHeight: '160%',
                    letterSpacing: '.15px',
                    color: isDark
                      ? GlassColors.dark.textPrimary
                      : GlassColors.light.textPrimary,
                  }}
                >
                  Last Rewiews
                </Typography>
                <ButtonTertiary
                  onClick={() => navigate(`/reviews/user/${handyman.id}`)}
                  sx={{
                    fontSize: '14px',
                    ml: 'auto',
                  }}
                >
                  View All Rewiews
                </ButtonTertiary>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '10px',
                  mb: '14px',
                  '& .bage': {
                    display: 'none',
                  },
                }}
              >
                <ReviewsList
                  reviews={lastReviews} // Show only first 2 reviews
                  loading={reviewsLoading}
                  error={''}
                  totalCount={userReviews.length}
                  showActions={false}
                  emptyMessage='No reviews yet'
                  title=''
                />
              </Box>
            </Box>
          )}
        </Collapse>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <ButtonTertiary
            onClick={() => toggleCard(handyman.id)}
            sx={{
              '&&': {
                fontSize: '14px',
                height: { xs: '32px', md: '40px' },
              },
            }}
          >
            {cardDetailsOpened ? 'Close Details' : 'View Details'}
          </ButtonTertiary>
          {/* Create Order Button */}
          {isNotHandyman && (
            <NeumorphicButton
              onClick={handleActionClick}
              disabled={isInvited}
              sx={{
                padding: '8px 24px',
                height: { xs: '32px', md: '40px' },
                width: 'auto',

                '&&': { fontSize: '14px' },
              }}
              startIcon={isInvited ? <CheckIcon sx={{ fontSize: 18 }} /> : null}
            >
              {actionButtonTitle}
            </NeumorphicButton>
          )}
        </Box>
      </Box>
    );
  },
);

const mapStateToProps = (state: HandymanAppState | any) => ({
  isLoading: state.userPortfolio.isLoading,
  isListUpdated: state.userPortfolio.isListUpdated,
  userReviews: state.review.userReviews,
});
export default connect(mapStateToProps, {})(HandymanCard);
